/**
 * Picks the source for a recorded interview video (intro selfie, question video).
 *
 * Staging/production: the backend returns a short-lived signed bucket URL and
 * the <video> element streams it directly (range requests, playback starts
 * right away). Local dev, an older backend, or a signing failure: no stream
 * URL, so the file is downloaded through the authenticated API as before.
 *
 * If a stream fails while playing (typically the signed URL expired), we ask
 * for a fresh URL once and resume where the viewer was; if that also fails we
 * fall back to the API download.
 */

export type StreamableVideoStatus = 'loading' | 'loaded' | 'none' | 'error';

export interface StreamableVideoState {
  status: StreamableVideoStatus;
  src: string | null;
}

export interface VideoSourceResolution {
  /** Signed bucket URL, or null when the file must be loaded through the API. */
  streamUrl: string | null;
  /** Seconds to seek to once the video metadata is loaded. */
  startAt?: number;
}

export interface StreamableVideoLoaders {
  /** Resolves a fresh source; null means there is no video to show. */
  resolveSource: () => Promise<VideoSourceResolution | null>;
  /** Downloads the file through the authenticated API. Throws on failure. */
  fetchBlob: () => Promise<Blob>;
}

export interface ObjectUrlApi {
  createObjectURL: (blob: Blob) => string;
  revokeObjectURL: (url: string) => void;
}

export type PlayableVideo = Pick<HTMLVideoElement, 'currentTime' | 'paused' | 'play'>;

export class StreamableVideoSource {
  private disposed = false;
  private mode: 'stream' | 'blob' | null = null;
  private streamRetried = false;
  private recovering = false;
  private blobUrl: string | null = null;
  private seekOnLoad = 0;
  private resumePlayback = false;
  private state: StreamableVideoState = { status: 'none', src: null };

  constructor(
    private readonly getLoaders: () => StreamableVideoLoaders,
    private readonly onChange: (state: StreamableVideoState) => void,
    private readonly urlApi: ObjectUrlApi = URL,
  ) {}

  getState(): StreamableVideoState {
    return this.state;
  }

  async load(): Promise<void> {
    this.setState({ status: 'loading', src: null });
    try {
      const resolution = await this.getLoaders().resolveSource();
      if (this.disposed) return;
      if (!resolution) {
        this.setState({ status: 'none', src: null });
        return;
      }
      this.seekOnLoad = Math.max(0, resolution.startAt ?? 0);
      if (resolution.streamUrl) {
        this.mode = 'stream';
        this.setState({ status: 'loaded', src: resolution.streamUrl });
        return;
      }
      await this.loadBlob();
    } catch {
      this.setState({ status: 'error', src: null });
    }
  }

  /** Call from the video's loadedmetadata event. */
  handleLoadedMetadata(video: PlayableVideo): void {
    if (this.disposed) return;
    if (this.seekOnLoad > 0) {
      video.currentTime = this.seekOnLoad;
    }
    this.seekOnLoad = 0;
    if (this.resumePlayback) {
      this.resumePlayback = false;
      video.play()?.catch(() => undefined);
    }
  }

  /** Call from the video's error event. Only stream failures are recovered. */
  async handleError(video: PlayableVideo): Promise<void> {
    if (this.disposed || this.mode !== 'stream' || this.recovering) return;
    this.recovering = true;
    if (video.currentTime > 0) {
      this.seekOnLoad = video.currentTime;
    }
    this.resumePlayback = !video.paused;
    const failedSrc = this.state.src;

    try {
      if (!this.streamRetried) {
        this.streamRetried = true;
        const resolution = await this.getLoaders().resolveSource();
        if (this.disposed) return;
        if (resolution?.streamUrl && resolution.streamUrl !== failedSrc) {
          this.setState({ status: 'loaded', src: resolution.streamUrl });
          return;
        }
      }
      this.setState({ status: 'loading', src: null });
      await this.loadBlob();
    } catch {
      this.setState({ status: 'error', src: null });
    } finally {
      this.recovering = false;
    }
  }

  dispose(): void {
    this.disposed = true;
    this.revokeBlob();
  }

  private async loadBlob(): Promise<void> {
    const blob = await this.getLoaders().fetchBlob();
    if (this.disposed) return;
    const url = this.urlApi.createObjectURL(blob);
    this.revokeBlob();
    this.blobUrl = url;
    this.mode = 'blob';
    this.setState({ status: 'loaded', src: url });
  }

  private revokeBlob(): void {
    if (this.blobUrl) {
      this.urlApi.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
  }

  private setState(state: StreamableVideoState): void {
    if (this.disposed) return;
    this.state = state;
    this.onChange(state);
  }
}
